'use client';

import type { QuantityDiscount } from '@eliadmin/core/src/api/products/types/product-types';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { getProductQuantityRules } from '@eliadmin/core/src/utils/product-utils';
import { useMemo } from 'react';

export type ProductPriceView = {
  /** Current price incl. unit suffix („71,90 €“, „2,50 € / m²“). */
  current: string;
  /** Struck-through price (list price or price before the quantity discount). */
  previous?: string;
  /** „18 €“ – difference between `previous` and `current`. */
  saving?: string;
  /** „MOC 89,90 €“ for wholesalers. */
  retail?: string;
  /** „s DPH · 58,46 € bez DPH“ */
  vatNote: string;
  /** Products sold in packs (`custom_quantity`): content and total of the selected packs. */
  packs?: { content: string; contentPrice: string; total: string };
};

const applyQuantityDiscount = (price: number | undefined, discount?: QuantityDiscount) => {
  if (price == null || !discount) return price;
  if (discount.discount_type === 'fixed') return Math.max(0, price - discount.discount);
  return Math.max(0, price - (price * discount.discount) / 100);
};

const formatQuantity = (value: number) => Number(value.toFixed(6)).toString();

/**
 * Prices of the buy box with the arithmetic of the core `ProductDetailPrice`: selected variant,
 * VAT mode of the store, services, quantity discount tiers and packs (`custom_quantity`).
 */
export function useProductPrice(): ProductPriceView {
  const { t } = useTranslations();
  const settings = useGlobalStore(state => state.templateSettings.product_detail);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const me = useCustomerStore(state => state.me);
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const selectedServices = useVariantsStore(state => state.selectedServices);
  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);

  const quantityRules = useMemo(
    () => getProductQuantityRules(product, selectedVariant),
    [product, selectedVariant],
  );
  const quantityDiscounts = useMemo(
    () =>
      (product.quantity_discounts ?? [])
        .filter(discount => Number(discount.min_quantity) > 0)
        .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity)),
    [product.quantity_discounts],
  );

  const format = (price?: number) => formatPrice(price, currency, currencySettings);

  const priceWithoutVatFirst = !!settings?.price_without_vat_first?.enabled;
  const priceDisplay = getProductPriceDisplay(
    (product.variants?.length ? selectedVariant : product) ?? product,
    { isWholesaler: me?.customer.is_wholesaler, priceWithoutVatFirst },
  );

  const rawCustomQuantity = Number(quantityRules?.custom_quantity ?? 1);
  const customQuantity =
    Number.isFinite(rawCustomQuantity) && rawCustomQuantity > 0 ? rawCustomQuantity : 1;
  const hasCustomQuantity = customQuantity !== 1;
  const customUnit = quantityRules?.unit || product.unit || 'm²';
  const suffix = hasCustomQuantity
    ? ` / ${customUnit}`
    : settings?.show_unit?.enabled
      ? ` / ${product.unit}`
      : '';

  const servicesPrice = (withVat: boolean) =>
    selectedServices.reduce(
      (sum, service) => sum + Number(withVat ? service.price_with_vat : service.price),
      0,
    );

  const inRange = quantityDiscounts.filter(
    discount =>
      selectedQuantity >= Number(discount.min_quantity ?? 0) &&
      selectedQuantity <=
        (discount.max_quantity == null ? Number.POSITIVE_INFINITY : Number(discount.max_quantity)),
  );
  const byTierDesc = (a: QuantityDiscount, b: QuantityDiscount) =>
    Number(b.min_quantity) - Number(a.min_quantity);
  const activeDiscount = (
    inRange.length
      ? inRange
      : quantityDiscounts.filter(discount => Number(discount.min_quantity) <= selectedQuantity)
  ).sort(byTierDesc)[0];

  const withServices = (price: number | undefined, withVat: boolean) =>
    price == null ? undefined : price + servicesPrice(withVat);

  const mainUsesVat = !priceWithoutVatFirst;
  const mainBefore = withServices(priceDisplay.current, mainUsesVat);
  const mainAfter = applyQuantityDiscount(mainBefore, activeDiscount);
  const retailAfter = applyQuantityDiscount(
    withServices(priceDisplay.mocPrice, mainUsesVat),
    activeDiscount,
  );
  const listPrice = withServices(priceDisplay.beforeDiscount, mainUsesVat);
  const secondary = applyQuantityDiscount(
    withServices(priceDisplay.secondary, priceDisplay.secondaryIncludesVat),
    activeDiscount,
  );

  const previousRaw = priceDisplay.showPreviousPrice
    ? listPrice
    : activeDiscount
      ? mainBefore
      : undefined;
  const savingRaw =
    previousRaw != null && mainAfter != null && previousRaw > mainAfter
      ? roundPrice(previousRaw - mainAfter, Number(currencySettings?.rounding) || 2)
      : undefined;

  const vatLabel = (withVat: boolean) => t(withVat ? 'text1122' : 'text443');
  const secondaryNote =
    settings?.show_price_without_vat?.enabled && secondary != null
      ? ` · ${priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}${format(
          secondary * (hasCustomQuantity ? customQuantity : 1),
        )} ${vatLabel(priceDisplay.secondaryIncludesVat)}${
          hasCustomQuantity ? ` / ${formatQuantity(customQuantity)} ${customUnit}` : suffix
        }`
      : '';

  return {
    current: `${format(mainAfter)}${suffix}`,
    previous: previousRaw != null ? `${format(previousRaw)}${suffix}` : undefined,
    saving:
      savingRaw != null
        ? formatPrice(
            savingRaw,
            currency,
            currencySettings && Number.isInteger(savingRaw)
              ? { ...currencySettings, decimal_places: '0' }
              : currencySettings,
          )
        : undefined,
    retail:
      priceDisplay.showMocVocPrices && retailAfter != null
        ? `${t('text1926')} ${format(retailAfter)}${suffix}`
        : undefined,
    vatNote: `${vatLabel(mainUsesVat)}${secondaryNote}`,
    packs:
      hasCustomQuantity && mainAfter != null
        ? {
            content: `${formatQuantity(customQuantity)} ${customUnit}`,
            contentPrice: format(mainAfter * customQuantity),
            total: format(mainAfter * customQuantity * selectedQuantity),
          }
        : undefined,
  };
}
